import React, { FC, useCallback } from 'react';
import { Badge } from '@ordr-as/umami-react/badge';
import { Button } from '@ordr-as/umami-react/button';
import { SearchBar } from '@ordr-as/umami-react/search-bar';
import { MenuSectionDto } from '../../../../generated-api';
import classNames from 'classnames';
import { useSearch } from '../../../context/SearchContext';
import SelectMenuMobile from './SelectMenuMobile';
import { useLocation } from '../../../context/LocationContext';
import { getMappedMenusByLocation } from './mappedMenusByLocation';
import ExpandHeaderButton from '../../../components/ExpandHeaderButton';
import config from '../../../config';

interface Props {
  menuSections: MenuSectionDto[];
  selectedSectionId?: string;
  onSectionSelected: (sectionId: string) => void;
  overflowRef?: React.RefObject<HTMLDivElement>;
  showOverflowIcon?: boolean;
  onOverflowIconClick: () => void;
  isExpanded?: boolean;
}

const MenuSectionsMobile: FC<Props> = ({
  menuSections,
  onSectionSelected,
  selectedSectionId,
  overflowRef,
  showOverflowIcon,
  onOverflowIconClick,
  isExpanded,
}) => {
  const { searchText, setSearchText, setShouldExpandSearchBar, handleSearchSubmit, searchError, setSearchError } =
    useSearch();
  const { location } = useLocation();
  const locationMenus = getMappedMenusByLocation(menuSections);
  const selectedLocationId =
    menuSections.find((section) => section.sectionId === selectedSectionId)?.locationId || menuSections[0]?.locationId;

  const handleSectionClick = (menuSection: MenuSectionDto) => {
    onSectionSelected(menuSection.sectionId);
  };

  const handleSelectedLocationMenu = (locationId: number) => {
    const sectionToSelect = locationMenus.find((locationMenu) => locationMenu.locationId === locationId)?.sections[0];

    if (!sectionToSelect) return;

    handleSectionClick(sectionToSelect);
  };

  const handleSearchTextChange = useCallback(
    (value: string) => {
      setSearchText(value);
      // Clear error when user starts typing
      if (searchError) {
        setSearchError(undefined);
      }
    },
    [setSearchText, searchError, setSearchError]
  );

  const colorOfItems = (active: boolean) =>
    classNames({
      'bg-purple-lightest text-bold': active,
    });

  const sections = location.isFoodCourt
    ? menuSections.filter((section) => section.locationId === selectedLocationId)
    : menuSections;

  return (
    <>
      <div className="flex flex-col col-span-full p-2 bg-brand-white mb-[-16px]">
        <SearchBar
          setActive={setShouldExpandSearchBar}
          fullWidth
          value={searchText}
          setValue={handleSearchTextChange}
          onSubmit={handleSearchSubmit}
          aria-label="Search for product"
        />
        {searchError && <div className="mt-2 text-sm text-orange-darkest px-2">{searchError}</div>}
      </div>
      <aside className="col-span-full bg-brand-white">
        {location.isFoodCourt && (
          <SelectMenuMobile
            locationMenus={locationMenus}
            selectedLocationId={selectedLocationId}
            setSelectedLocationId={handleSelectedLocationMenu}
          />
        )}
        <div className="relative bg-brand-white">
          {config.showSubsections && showOverflowIcon && (
            <ExpandHeaderButton onExpandHeaderClick={onOverflowIconClick} isPhone={true} isExpanded={isExpanded} />
          )}
          <div
            ref={overflowRef}
            className={classNames('flex p-2 gap-2 bg-brand-white', {
              'overflow-x-auto flex-nowrap': !isExpanded,
              'flex-wrap overflow-visible pr-7': isExpanded,
            })}
          >
            {sections.map((menuSection) => {
              const active = menuSection.sectionId === selectedSectionId;

              const matchCount =
                menuSection.items.length + menuSection.subsections.reduce((sum, sub) => sum + sub.items.length, 0);

              return (
                <Button
                  className={classNames(
                    'flex flex-row justify-between truncate text-small bg-grey-lightest rounded',
                    {
                      'flex-shrink-0': !isExpanded,
                    },
                    colorOfItems(active)
                  )}
                  key={menuSection.sectionId}
                  onClick={() => handleSectionClick(menuSection)}
                >
                  {menuSection.eposDisplayColor && (
                    <div className="flex h-full w-3" style={{ backgroundColor: menuSection.eposDisplayColor }} />
                  )}
                  <div className="flex items-center p-2">
                    {menuSection.name}
                    {searchText.length !== 0 ? (
                      <Badge text={matchCount.toString()} className="ml-2 bg-brand-white" />
                    ) : null}
                  </div>
                </Button>
              );
            })}
          </div>
        </div>
      </aside>
    </>
  );
};
export default MenuSectionsMobile;
